'use client';

import React, { useState, useRef, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Search, ChevronDown, X, ChevronLeft, ChevronRight,
  ClipboardList, Plus, Check, Clock, CheckCircle2,
  AlertCircle, Package2, Lightbulb, TrendingUp, AlertTriangle,
  Info
} from 'lucide-react';

// ─── Kit Master Data ──────────────────────────────────────────────────────────
interface KitMasterOption {
  id: string;
  kitCode: string;
  kitName: string;
  version: string;
  status: 'Active' | 'Inactive' | 'Draft';
  components: { itemCode: string; itemName: string; requiredQty: number }[];
}

const KIT_MASTER_OPTIONS: KitMasterOption[] = [
  {
    id: '1',
    kitCode: 'KIT-001',
    kitName: 'Basic First Aid Kit',
    version: 'v2.5.0',
    status: 'Active',
    components: [
      { itemCode: 'ITM-001', itemName: 'Adhesive Bandage (Box)', requiredQty: 2 },
      { itemCode: 'ITM-002', itemName: 'Antiseptic Wipes (Pack)', requiredQty: 1 },
      { itemCode: 'ITM-003', itemName: 'Sterile Gauze Pad', requiredQty: 4 },
      { itemCode: 'ITM-004', itemName: 'Medical Tape Roll', requiredQty: 1 },
    ],
  },
  {
    id: '2',
    kitCode: 'KIT-002',
    kitName: 'Surgical Procedure Kit',
    version: 'v2.5.1',
    status: 'Active',
    components: [
      { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', requiredQty: 3 },
      { itemCode: 'ITM-006', itemName: 'Scalpel Blade No.22', requiredQty: 2 },
      { itemCode: 'ITM-003', itemName: 'Sterile Gauze Pad', requiredQty: 6 },
      { itemCode: 'ITM-007', itemName: 'Suture Kit', requiredQty: 1 },
    ],
  },
  {
    id: '3',
    kitCode: 'KIT-003',
    kitName: 'IV Therapy Kit',
    version: 'v2.4.1',
    status: 'Inactive',
    components: [
      { itemCode: 'ITM-008', itemName: 'IV Cannula 18G', requiredQty: 2 },
      { itemCode: 'ITM-009', itemName: 'IV Drip Set', requiredQty: 1 },
      { itemCode: 'ITM-010', itemName: 'Alcohol Swab (Pack)', requiredQty: 1 },
    ],
  },
];

// ─── Item Inventory (current stock) ──────────────────────────────────────────
interface ItemStock {
  itemCode: string;
  itemName: string;
  availableQty: number;
}

const ITEM_INVENTORY: ItemStock[] = [
  { itemCode: 'ITM-001', itemName: 'Adhesive Bandage (Box)', availableQty: 120 },
  { itemCode: 'ITM-002', itemName: 'Antiseptic Wipes (Pack)', availableQty: 45 },
  { itemCode: 'ITM-003', itemName: 'Sterile Gauze Pad', availableQty: 200 },
  { itemCode: 'ITM-004', itemName: 'Medical Tape Roll', availableQty: 30 },
  { itemCode: 'ITM-005', itemName: 'Surgical Gloves (Pair)', availableQty: 90 },
  { itemCode: 'ITM-006', itemName: 'Scalpel Blade No.22', availableQty: 40 },
  { itemCode: 'ITM-007', itemName: 'Suture Kit', availableQty: 18 },
  { itemCode: 'ITM-008', itemName: 'IV Cannula 18G', availableQty: 0 },
  { itemCode: 'ITM-009', itemName: 'IV Drip Set', availableQty: 12 },
  { itemCode: 'ITM-010', itemName: 'Alcohol Swab (Pack)', availableQty: 55 },
];

// ─── Requisition Types ────────────────────────────────────────────────────────
type RequisitionStatus = 'Pending' | 'In Progress' | 'Completed' | 'Cancelled';

interface RequisitionRecord {
  id: string;
  requisitionNo: string;
  kitCode: string;
  kitName: string;
  version: string;
  requestedQty: number;
  kitsCreated: number;
  requestedDate: string;
  completedDate?: string;
  status: RequisitionStatus;
  requestedBy: string;
}

const INITIAL_REQUISITIONS: RequisitionRecord[] = [
  { id: '1', requisitionNo: 'REQ-20260710-001', kitCode: 'KIT-001', kitName: 'Basic First Aid Kit', version: 'v2.5.0', requestedQty: 30, kitsCreated: 30, requestedDate: '10 Jul 2026', completedDate: '12 Jul 2026', status: 'Completed', requestedBy: 'Prasun Sharma' },
  { id: '2', requisitionNo: 'REQ-20260711-002', kitCode: 'KIT-002', kitName: 'Surgical Procedure Kit', version: 'v2.5.1', requestedQty: 20, kitsCreated: 15, requestedDate: '11 Jul 2026', completedDate: '11 Jul 2026', status: 'In Progress', requestedBy: 'Prasun Sharma' },
  { id: '3', requisitionNo: 'REQ-20260712-003', kitCode: 'KIT-003', kitName: 'IV Therapy Kit', version: 'v2.4.1', requestedQty: 10, kitsCreated: 0, requestedDate: '12 Jul 2026', status: 'Pending', requestedBy: 'Prasun Sharma' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function computeMaxKits(kit: KitMasterOption): { maxKits: number; limitingItem: string | null; breakdown: { itemCode: string; itemName: string; requiredQty: number; availableQty: number; canMake: number }[] } {
  const breakdown = kit.components.map((comp) => {
    const stock = ITEM_INVENTORY.find((i) => i.itemCode === comp.itemCode);
    const availableQty = stock?.availableQty ?? 0;
    const canMake = comp.requiredQty > 0 ? Math.floor(availableQty / comp.requiredQty) : 0;
    return { ...comp, availableQty, canMake };
  });
  const maxKits = breakdown.length > 0 ? Math.min(...breakdown.map((b) => b.canMake)) : 0;
  const limiting = breakdown.find((b) => b.canMake === maxKits && maxKits < 9999);
  return { maxKits, limitingItem: limiting?.itemName ?? null, breakdown };
}

const reqStatusColor = (status: RequisitionStatus) => {
  if (status === 'Completed') return 'text-emerald-600 bg-emerald-50 border border-emerald-200';
  if (status === 'In Progress') return 'text-blue-600 bg-blue-50 border border-blue-200';
  if (status === 'Pending') return 'text-amber-600 bg-amber-50 border border-amber-200';
  return 'text-gray-500 bg-gray-50 border border-gray-200';
};

const reqStatusIcon = (status: RequisitionStatus) => {
  if (status === 'Completed') return <CheckCircle2 size={13} className="text-emerald-600" />;
  if (status === 'In Progress') return <Clock size={13} className="text-blue-600" />;
  if (status === 'Pending') return <AlertCircle size={13} className="text-amber-600" />;
  return <X size={13} className="text-gray-500" />;
};

// ─── Searchable Filter ────────────────────────────────────────────────────────
interface SearchableFilterProps {
  label: string;
  value: string;
  options: string[];
  onChange: (val: string) => void;
}

function SearchableFilter({ label, value, options, onChange }: SearchableFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary/50 min-w-[130px] ${value ? 'border-primary/50 bg-primary/5 text-foreground' : 'border-border bg-card text-muted-foreground'}`}
      >
        <span className="flex-1 text-left truncate">{value || label}</span>
        <ChevronDown size={13} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-48 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors ${!value ? 'text-primary font-semibold bg-primary/5' : 'text-muted-foreground'}`}
            >
              All {label}s
            </button>
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => { onChange(opt); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors ${value === opt ? 'text-primary font-semibold bg-primary/5' : 'text-foreground'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Kit Selector Dropdown ────────────────────────────────────────────────────
interface KitSelectorProps {
  value: string;
  onChange: (kitId: string) => void;
}

function KitSelector({ value, onChange }: KitSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeKits = KIT_MASTER_OPTIONS.filter((k) => k.status === 'Active');
  const filteredKits = activeKits.filter(
    (k) =>
      !query ||
      k.kitName.toLowerCase().includes(query.toLowerCase()) ||
      k.kitCode.toLowerCase().includes(query.toLowerCase())
  );

  const selected = KIT_MASTER_OPTIONS.find((k) => k.id === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary/50 ${value ? 'border-primary/50 bg-primary/5 text-foreground' : 'border-border bg-card text-muted-foreground'}`}
      >
        <Package2 size={14} className="flex-shrink-0 text-muted-foreground" />
        <span className="flex-1 text-left truncate">
          {selected ? `${selected.kitName} (${selected.kitCode})` : 'Select Kit Name…'}
        </span>
        <ChevronDown size={13} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search kit name or code…"
                className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filteredKits.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">No active kits found</div>
            ) : filteredKits.map((kit) => {
              const { maxKits } = computeMaxKits(kit);
              return (
                <button
                  key={kit.id}
                  type="button"
                  onClick={() => { onChange(kit.id); setOpen(false); setQuery(''); }}
                  className={`w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0 ${value === kit.id ? 'bg-primary/5' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className={`text-xs font-semibold ${value === kit.id ? 'text-primary' : 'text-foreground'}`}>{kit.kitName}</p>
                      <p className="text-2xs text-muted-foreground mt-0.5">{kit.kitCode}</p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-2xs font-medium text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full flex-shrink-0">{kit.version}</span>
                      <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${maxKits > 0 ? 'text-emerald-600 bg-emerald-50' : 'text-red-500 bg-red-50'}`}>
                        Can make: {maxKits}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Kit Suggestion Panel ─────────────────────────────────────────────────────
interface KitSuggestionPanelProps {
  kit: KitMasterOption;
  requestedQty: number;
}

function KitSuggestionPanel({ kit, requestedQty }: KitSuggestionPanelProps) {
  const { maxKits, limitingItem, breakdown } = computeMaxKits(kit);
  const canFulfill = maxKits >= requestedQty;
  const shortage = requestedQty > 0 ? requestedQty - maxKits : 0;

  return (
    <div className={`rounded-xl border p-4 ${canFulfill ? 'border-emerald-200 bg-emerald-50/60' : maxKits > 0 ? 'border-amber-200 bg-amber-50/60' : 'border-red-200 bg-red-50/60'}`}>
      {/* Suggestion header */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${canFulfill ? 'bg-emerald-100' : maxKits > 0 ? 'bg-amber-100' : 'bg-red-100'}`}>
          {canFulfill ? (
            <TrendingUp size={16} className="text-emerald-600" />
          ) : maxKits > 0 ? (
            <AlertTriangle size={16} className="text-amber-600" />
          ) : (
            <AlertTriangle size={16} className="text-red-500" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-bold ${canFulfill ? 'text-emerald-700' : maxKits > 0 ? 'text-amber-700' : 'text-red-600'}`}>
              {canFulfill
                ? `✓ Inventory sufficient — up to ${maxKits} kit${maxKits !== 1 ? 's' : ''} can be made`
                : maxKits > 0
                ? `⚠ Partial stock — only ${maxKits} kit${maxKits !== 1 ? 's' : ''} can be made`
                : '✗ Insufficient stock — cannot make any kits'}
            </p>
          </div>
          {requestedQty > 0 && !canFulfill && maxKits > 0 && (
            <p className="text-xs text-amber-600 mt-0.5">
              Shortfall of <strong>{shortage}</strong> kit{shortage !== 1 ? 's' : ''} against requested qty of {requestedQty}
            </p>
          )}
          {requestedQty > 0 && !canFulfill && maxKits === 0 && (
            <p className="text-xs text-red-500 mt-0.5">
              Requested {requestedQty} kit{requestedQty !== 1 ? 's' : ''} — stock replenishment needed
            </p>
          )}
          {limitingItem && !canFulfill && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Info size={11} /> Limiting item: <span className="font-semibold text-foreground">{limitingItem}</span>
            </p>
          )}
        </div>
      </div>

      {/* Component breakdown */}
      <div className="border-t border-border/40 pt-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Lightbulb size={11} /> Component Inventory Breakdown
        </p>
        <div className="space-y-1.5">
          {breakdown.map((item) => {
            const pct = requestedQty > 0 ? Math.min(100, (item.availableQty / (item.requiredQty * requestedQty)) * 100) : 100;
            const isLimiting = item.canMake === maxKits && !canFulfill;
            return (
              <div key={item.itemCode} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isLimiting ? 'bg-red-100/60 border border-red-200' : 'bg-white/60 border border-border/30'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-xs font-medium truncate ${isLimiting ? 'text-red-700' : 'text-foreground'}`}>
                      {item.itemName}
                      {isLimiting && <span className="ml-1.5 text-2xs font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded-full">Limiting</span>}
                    </span>
                    <span className="text-2xs text-muted-foreground flex-shrink-0">
                      <span className={`font-semibold ${item.availableQty === 0 ? 'text-red-500' : item.availableQty < item.requiredQty * (requestedQty || 1) ? 'text-amber-600' : 'text-emerald-600'}`}>{item.availableQty}</span>
                      {' '}avail / need {item.requiredQty * (requestedQty || 1)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${item.availableQty === 0 ? 'bg-red-400' : pct < 100 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <span className={`text-xs font-bold flex-shrink-0 w-12 text-right ${item.canMake === 0 ? 'text-red-500' : 'text-foreground'}`}>
                  ×{item.canMake}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-2xs text-muted-foreground mt-2 flex items-center gap-1">
          <Info size={10} /> ×N = how many kits this item alone can support
        </p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function RequisitionPage() {
  const [selectedKitId, setSelectedKitId] = useState('');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [requisitions, setRequisitions] = useState<RequisitionRecord[]>(INITIAL_REQUISITIONS);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const historyPageSize = 10;

  const selectedKit = KIT_MASTER_OPTIONS.find((k) => k.id === selectedKitId);
  const requestedQtyNum = parseInt(qty) || 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedKitId || !qty || parseInt(qty) < 1) return;

    const kit = KIT_MASTER_OPTIONS.find((k) => k.id === selectedKitId)!;
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')} ${now.toLocaleString('en-GB', { month: 'short' })} ${now.getFullYear()}`;
    const newReq: RequisitionRecord = {
      id: String(Date.now()),
      requisitionNo: `REQ-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(requisitions.length + 1).padStart(3, '0')}`,
      kitCode: kit.kitCode,
      kitName: kit.kitName,
      version: kit.version,
      requestedQty: parseInt(qty),
      kitsCreated: 0,
      requestedDate: dateStr,
      status: 'Pending',
      requestedBy: 'Prasun Sharma',
    };
    setRequisitions((prev) => [newReq, ...prev]);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setSelectedKitId('');
      setQty('');
      setNotes('');
    }, 2500);
  };

  const filteredHistory = requisitions.filter((r) => {
    const matchSearch = !historySearch ||
      r.requisitionNo.toLowerCase().includes(historySearch.toLowerCase()) ||
      r.kitName.toLowerCase().includes(historySearch.toLowerCase()) ||
      r.kitCode.toLowerCase().includes(historySearch.toLowerCase());
    const matchStatus = !historyStatusFilter || r.status === historyStatusFilter;
    return matchSearch && matchStatus;
  });

  const totalHistoryPages = Math.max(1, Math.ceil(filteredHistory.length / historyPageSize));
  const safeHistoryPage = Math.min(historyPage, totalHistoryPages);
  const historyStart = (safeHistoryPage - 1) * historyPageSize;
  const paginatedHistory = filteredHistory.slice(historyStart, historyStart + historyPageSize);

  const totalRequested = requisitions.reduce((s, r) => s + r.requestedQty, 0);
  const totalCreated = requisitions.reduce((s, r) => s + r.kitsCreated, 0);
  const pendingCount = requisitions.filter((r) => r.status === 'Pending').length;
  const inProgressCount = requisitions.filter((r) => r.status === 'In Progress').length;

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen bg-background">
        <div className="flex-1 flex flex-col gap-5 p-5">

          {/* Page Header */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <ClipboardList size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Requisition</h1>
              <p className="text-xs text-muted-foreground">Raise production requisitions and track kit creation progress</p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Total Requisitions</p>
              <p className="text-xl font-bold text-foreground mt-0.5">{requisitions.length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Kits Requested</p>
              <p className="text-xl font-bold text-foreground mt-0.5">{totalRequested}</p>
            </div>
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Kits Created</p>
              <p className="text-xl font-bold text-emerald-600 mt-0.5">{totalCreated}</p>
            </div>
            <div className="bg-card border border-border rounded-xl px-4 py-3">
              <p className="text-xs text-muted-foreground">Pending / In Progress</p>
              <p className="text-xl font-bold text-amber-600 mt-0.5">{pendingCount + inProgressCount}</p>
            </div>
          </div>

          {/* New Requisition Form */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border bg-muted/20 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Plus size={15} className="text-primary" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-foreground">New Requisition</h2>
                <p className="text-xs text-muted-foreground">Select a kit — system will suggest how many can be made from current inventory</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Left: Form fields */}
                <div className="flex flex-col gap-4">
                  {/* Kit Name */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Kit Name <span className="text-red-500">*</span>
                    </label>
                    <KitSelector value={selectedKitId} onChange={(id) => { setSelectedKitId(id); setQty(''); }} />
                    {selectedKit && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Kit Code:</span>
                        <span className="font-mono text-xs font-semibold text-foreground bg-muted px-2 py-0.5 rounded">{selectedKit.kitCode}</span>
                        <span className="text-xs text-muted-foreground">Version:</span>
                        <span className="text-xs font-medium text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">{selectedKit.version}</span>
                      </div>
                    )}
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Quantity to Produce <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      placeholder="Enter quantity…"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                    {selectedKit && (() => {
                      const { maxKits } = computeMaxKits(selectedKit);
                      return (
                        <p className={`text-xs mt-1.5 font-medium flex items-center gap-1 ${maxKits > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          <Lightbulb size={11} />
                          Suggested max: <strong>{maxKits}</strong> kit{maxKits !== 1 ? 's' : ''} based on current inventory
                        </p>
                      );
                    })()}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Notes (optional)</label>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add a note for production team…"
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={!selectedKitId || !qty || parseInt(qty) < 1 || submitted}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitted ? (
                        <><Check size={15} /> Requisition Submitted</>
                      ) : (
                        <><Plus size={15} /> Submit Requisition</>
                      )}
                    </button>
                    {submitted && (
                      <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                        <CheckCircle2 size={13} /> Added to history below
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: Kit Suggestion Panel */}
                <div>
                  {selectedKit ? (
                    <KitSuggestionPanel kit={selectedKit} requestedQty={requestedQtyNum} />
                  ) : (
                    <div className="h-full min-h-[180px] flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 text-center px-6 py-8">
                      <Lightbulb size={28} className="text-muted-foreground/40 mb-2" />
                      <p className="text-sm font-medium text-muted-foreground">Select a kit to see inventory suggestion</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">System will show how many kits can be made from current item stock</p>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Requisition History */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border bg-muted/20 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ClipboardList size={15} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground">Requisition History</h2>
                  <p className="text-xs text-muted-foreground">{filteredHistory.length} requisition{filteredHistory.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search requisition…"
                    value={historySearch}
                    onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                    className="pl-7 pr-3 py-2 rounded-lg border border-border bg-background text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 w-44"
                  />
                </div>
                <SearchableFilter
                  label="Status"
                  value={historyStatusFilter}
                  options={['Pending', 'In Progress', 'Completed', 'Cancelled']}
                  onChange={(v) => { setHistoryStatusFilter(v); setHistoryPage(1); }}
                />
                {(historySearch || historyStatusFilter) && (
                  <button
                    onClick={() => { setHistorySearch(''); setHistoryStatusFilter(''); setHistoryPage(1); }}
                    className="flex items-center gap-1 px-2.5 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <X size={11} /> Clear
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Req. No.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kit Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Version</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Requested Qty</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Kits Created</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Requested Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Requested By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedHistory.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-14 text-muted-foreground text-sm">No requisitions found.</td>
                    </tr>
                  ) : paginatedHistory.map((req, idx) => (
                    <tr key={req.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${idx % 2 !== 0 ? 'bg-muted/10' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">{req.requisitionNo}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground text-sm">{req.kitName}</p>
                        <p className="text-2xs text-muted-foreground mt-0.5">{req.kitCode}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">{req.version}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-foreground">{req.requestedQty}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${req.kitsCreated >= req.requestedQty ? 'text-emerald-600' : req.kitsCreated > 0 ? 'text-blue-600' : 'text-muted-foreground'}`}>
                            {req.kitsCreated}
                          </span>
                          <span className="text-xs text-muted-foreground">/ {req.requestedQty}</span>
                          {req.requestedQty > 0 && (
                            <div className="flex-1 min-w-[50px] max-w-[80px] h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${req.kitsCreated >= req.requestedQty ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min(100, (req.kitsCreated / req.requestedQty) * 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground">{req.requestedDate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground">{req.requestedBy}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${reqStatusColor(req.status)}`}>
                          {reqStatusIcon(req.status)}
                          {req.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* History Pagination */}
            {totalHistoryPages > 1 && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  {filteredHistory.length === 0 ? '0' : `${historyStart + 1}–${Math.min(historyStart + historyPageSize, filteredHistory.length)}`} of {filteredHistory.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    disabled={safeHistoryPage === 1}
                    className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: totalHistoryPages }, (_, i) => i + 1).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => setHistoryPage(pg)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${safeHistoryPage === pg ? 'bg-primary text-white' : 'border border-border text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                    >
                      {pg}
                    </button>
                  ))}
                  <button
                    onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                    disabled={safeHistoryPage === totalHistoryPages}
                    className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
